import React, { useState } from "react";
import {
  Alert,
  Button,
  Col,
  Descriptions,
  Grid,
  message,
  Modal,
  Row,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  FileImageOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { analyzeCreditImage, importCreditSchedule } from "./creditsService";
import { Credit } from "./types";

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const MAX_PAGES = 5;

type ExtractedScheduleRow = {
  period: number;
  date: string;
  disbursement: number;
  otherDisbursements: number;
  principalPaid: number;
  interestPaid: number;
  otherPayments: number;
  remainingPrincipal: number;
  description?: string;
};

type ExtractedCreditData = {
  creditName: string;
  totalAmount: number;
  currency: string;
  annualInterestRate: number;
  effectiveAnnualRate: number;
  annuity: number;
  startDate: string;
  disbursementDate: string;
  endDate: string;
  schedule: ExtractedScheduleRow[];
};

type PageInfo = {
  id: string;
  fileName: string;
  periodStart: number;
  periodEnd: number;
};

type Props = {
  open: boolean;
  clientId: string;
  token?: string;
  onCancel: () => void;
  onSuccess: (credit: Credit) => void;
};

const CreditImageImportModal: React.FC<Props> = ({
  open,
  clientId,
  token,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [currentFile, setCurrentFile] = useState<UploadFile[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [schedule, setSchedule] = useState<ExtractedScheduleRow[]>([]);
  const [header, setHeader] = useState<Partial<ExtractedCreditData>>({});
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revokePreviews = (list: UploadFile[]) => {
    list.forEach((f) => f.thumbUrl && URL.revokeObjectURL(f.thumbUrl));
  };

  const handleReset = () => {
    revokePreviews(currentFile);
    setCurrentFile([]);
    setPages([]);
    setSchedule([]);
    setHeader({});
    setError(null);
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  const beforeUpload = (rawFile: File) => {
    if (!["image/jpeg", "image/jpg", "image/png"].includes(rawFile.type)) {
      message.error(t("Only JPEG and PNG images are supported"));
      return Upload.LIST_IGNORE;
    }
    if (rawFile.size > 10 * 1024 * 1024) {
      message.error(t("Image must be smaller than 10MB"));
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleUploadChange: (info: { fileList: UploadFile[] }) => void = (info) => {
    const newList = info.fileList.slice(-1).map((f) => {
      if (!f.thumbUrl && f.originFileObj) {
        f.thumbUrl = URL.createObjectURL(f.originFileObj as File);
      }
      return f;
    });
    setCurrentFile(newList);
    setError(null);
  };

  const handleRemoveCurrentFile = (removedFile: UploadFile) => {
    if (removedFile.thumbUrl) URL.revokeObjectURL(removedFile.thumbUrl);
    setCurrentFile([]);
  };

  const handleAnalyzePage = async () => {
    const rawFile = currentFile[0]?.originFileObj as File | undefined;
    if (!rawFile) return;
    setAnalyzing(true);
    setError(null);
    try {
      const data: ExtractedCreditData = await analyzeCreditImage([rawFile], token);
      if (!data?.schedule?.length) {
        setError(t("No repayment schedule could be extracted from this image. Please try a clearer photo."));
        return;
      }

      const incoming = data.schedule;
      const incomingPeriods = incoming.map((r) => r.period).sort((a, b) => a - b);

      if (schedule.length > 0) {
        const expectedNext = schedule[schedule.length - 1].period + 1;
        if (incomingPeriods[0] !== expectedNext) {
          setError(
            t(
              "This page starts at period {{got}}, but period {{expected}} was expected next. Make sure you're uploading pages in order and haven't skipped or repeated a page.",
              { got: incomingPeriods[0], expected: expectedNext },
            ),
          );
          return;
        }
      }

      setSchedule((prev) => [...prev, ...incoming]);
      setPages((prev) => [
        ...prev,
        {
          id: currentFile[0].uid,
          fileName: currentFile[0].name || `page-${prev.length + 1}`,
          periodStart: incomingPeriods[0],
          periodEnd: incomingPeriods[incomingPeriods.length - 1],
        },
      ]);
      setHeader((prev) => ({
        creditName: prev.creditName || data.creditName,
        totalAmount: prev.totalAmount || data.totalAmount,
        currency: prev.currency || data.currency,
        annualInterestRate: prev.annualInterestRate ?? data.annualInterestRate,
        effectiveAnnualRate: prev.effectiveAnnualRate ?? data.effectiveAnnualRate,
        annuity: prev.annuity || data.annuity,
        startDate: prev.startDate || data.startDate,
        disbursementDate: prev.disbursementDate || data.disbursementDate,
        endDate: data.endDate || prev.endDate,
      }));

      revokePreviews(currentFile);
      setCurrentFile([]);
    } catch (err: any) {
      setError(err?.response?.data?.message || t("Failed to analyze image. Please try again."));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleRemoveLastPage = () => {
    const lastPage = pages[pages.length - 1];
    if (!lastPage) return;
    setSchedule((prev) => prev.filter((r) => r.period < lastPage.periodStart));
    setPages((prev) => prev.slice(0, -1));
    setError(null);
  };

  const handleImport = async () => {
    if (!schedule.length || !clientId || !header.totalAmount || !header.startDate) return;
    setImporting(true);
    try {
      const credit = await importCreditSchedule(
        {
          clientId,
          name: header.creditName || t("Imported credit"),
          totalAmount: header.totalAmount,
          annuity: header.annuity,
          annualInterestRate: header.annualInterestRate,
          effectiveAnnualRate: header.effectiveAnnualRate,
          startDate: header.startDate,
          disbursementDate: header.disbursementDate,
          endDate: header.endDate,
          currency: header.currency,
          schedule,
        },
        token,
      );
      message.success(t("Credit imported successfully"));
      handleReset();
      onSuccess(credit);
    } catch (err: any) {
      message.error(err?.response?.data?.message || t("Failed to import credit"));
    } finally {
      setImporting(false);
    }
  };

  const money = (val: number) =>
    (val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const scheduleColumns = [
    { title: t("Period"), dataIndex: "period", key: "period", width: 60 },
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      render: (val: string) => dayjs(val).format("DD.MM.YYYY"),
      width: 100,
    },
    {
      title: t("Disbursement"),
      dataIndex: "disbursement",
      key: "disbursement",
      render: (val: number, row: ExtractedScheduleRow) =>
        val || row.otherDisbursements ? money(val + row.otherDisbursements) : "-",
      width: 110,
    },
    {
      title: t("Principal"),
      dataIndex: "principalPaid",
      key: "principalPaid",
      render: (val: number) => (val ? money(val) : "-"),
      width: 100,
    },
    {
      title: t("Interest"),
      dataIndex: "interestPaid",
      key: "interestPaid",
      render: (val: number) => (val ? money(val) : "-"),
      width: 100,
    },
    {
      title: t("Other costs"),
      dataIndex: "otherPayments",
      key: "otherPayments",
      render: (val: number) => (val ? money(val) : "-"),
      width: 100,
    },
    {
      title: t("Remaining"),
      dataIndex: "remainingPrincipal",
      key: "remainingPrincipal",
      render: (val: number) => money(val),
      width: 110,
    },
    { title: t("Description"), dataIndex: "description", key: "description", width: 160 },
  ];

  const totalWithInterest = schedule.reduce(
    (sum, row) => sum + row.principalPaid + row.interestPaid + row.otherPayments + row.otherDisbursements,
    0,
  );

  const realInstallmentsCount = schedule.filter(
    (row) => row.period > 0 && row.principalPaid > 0,
  ).length;

  const canAddMorePages = pages.length < MAX_PAGES;

  const lastRemainingPrincipal = schedule.length
    ? schedule[schedule.length - 1].remainingPrincipal
    : 0;
  const scheduleLooksIncomplete =
    schedule.length > 0 &&
    !!header.totalAmount &&
    lastRemainingPrincipal > header.totalAmount * 0.01;

  const renderPageUpload = () => (
    <div style={{ padding: pages.length > 0 ? "12px 0 0" : "8px 0" }}>
      {pages.length > 0 && (
        <Text strong style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
          {t("Add next page")} ({pages.length}/{MAX_PAGES})
        </Text>
      )}
      <Dragger
        accept="image/jpeg,image/jpg,image/png"
        listType="picture-card"
        multiple={false}
        maxCount={1}
        fileList={currentFile}
        beforeUpload={beforeUpload}
        onChange={handleUploadChange}
        onRemove={handleRemoveCurrentFile}
        style={{ borderRadius: 8 }}
      >
        {currentFile.length >= 1 ? null : (
          <div style={{ padding: "16px 0" }}>
            <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
              <FileImageOutlined style={{ fontSize: 32, color: "#1890ff" }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 13, margin: "4px 0 0" }}>
              {pages.length === 0 ? t("Click or drag the first page here") : t("Click or drag the next page here")}
            </p>
          </div>
        )}
      </Dragger>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t("Send one page at a time - analyze it, check the table, then add the next page. Supports JPEG and PNG, max 10MB.")}
      </Text>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginTop: 12 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {currentFile.length > 0 && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Button
            type="primary"
            icon={<RobotOutlined />}
            size="large"
            loading={analyzing}
            onClick={handleAnalyzePage}
          >
            {analyzing ? t("Analyzing...") : t("Analyze this page")}
          </Button>
        </div>
      )}
    </div>
  );

  const renderAccumulated = () => (
    <div>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Title level={5} style={{ margin: 0 }}>
            <RobotOutlined style={{ marginRight: 8, color: "#1890ff" }} />
            {t("Extracted credit data")}
          </Title>
        </Col>
        <Col span={24}>
          {pages.map((p, idx) => (
            <Tag
              key={p.id}
              icon={<CheckCircleOutlined />}
              color="blue"
              closable={idx === pages.length - 1}
              onClose={handleRemoveLastPage}
              closeIcon={<CloseCircleOutlined />}
              style={{ marginBottom: 4 }}
            >
              {t("Page")} {idx + 1}: {t("periods")} {p.periodStart}-{p.periodEnd}
            </Tag>
          ))}
        </Col>
        <Col span={24}>
          <Descriptions bordered size="small" column={isMobile ? 1 : 2} style={{ fontSize: 13 }}>
            <Descriptions.Item label={t("Credit name")}>
              <strong>{header.creditName}</strong>
            </Descriptions.Item>
            <Descriptions.Item label={t("Currency")}>{header.currency}</Descriptions.Item>
            <Descriptions.Item label={t("Principal amount")}>
              <strong>{header.totalAmount ? money(header.totalAmount) : "-"}</strong>
            </Descriptions.Item>
            <Descriptions.Item label={t("Total with interest")}>
              <strong>{money(totalWithInterest)}</strong>
            </Descriptions.Item>
            <Descriptions.Item label={t("Annual interest rate")}>
              {header.annualInterestRate}%
            </Descriptions.Item>
            <Descriptions.Item label={t("Effective annual rate")}>
              {header.effectiveAnnualRate}%
            </Descriptions.Item>
            <Descriptions.Item label={t("Monthly annuity")}>
              {header.annuity ? money(header.annuity) : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Installments count")}>{realInstallmentsCount}</Descriptions.Item>
            <Descriptions.Item label={t("Start date")}>
              {header.startDate ? dayjs(header.startDate).format("DD.MM.YYYY") : "-"}
            </Descriptions.Item>
            <Descriptions.Item label={t("End date")}>
              {header.endDate ? dayjs(header.endDate).format("DD.MM.YYYY") : "-"}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col span={24}>
          <Title level={5} style={{ margin: "0 0 8px" }}>
            {t("Repayment schedule")} ({schedule.length} {t("rows")}, {realInstallmentsCount} {t("installments")})
          </Title>
          <Table
            dataSource={schedule}
            columns={scheduleColumns}
            rowKey="period"
            size="small"
            scroll={{ x: 780, y: 260 }}
            pagination={false}
            style={{ fontSize: 12 }}
          />
        </Col>
        {scheduleLooksIncomplete && (
          <Col span={24}>
            <Alert
              type="warning"
              showIcon
              message={t(
                "The remaining balance after the last row is still {{amount}} - this usually means the repayment plan isn't finished yet. Add the remaining pages before creating the credit.",
                { amount: money(lastRemainingPrincipal) },
              )}
            />
          </Col>
        )}
        {canAddMorePages && <Col span={24}>{renderPageUpload()}</Col>}
      </Row>
    </div>
  );

  return (
    <Modal
      open={open}
      title={
        <span>
          <CloudUploadOutlined style={{ marginRight: 8 }} />
          {t("Import credit from repayment plan image(s)")}
        </span>
      }
      width={isMobile ? "100%" : 800}
      onCancel={handleCancel}
      className="standardHeader standardFooter"
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t("Cancel")}
        </Button>,
        <Button
          key="import"
          type="primary"
          disabled={!schedule.length}
          loading={importing}
          onClick={handleImport}
          icon={<CloudUploadOutlined />}
        >
          {t("Create credit")}
        </Button>,
      ]}
      destroyOnClose
      styles={isMobile ? { body: { maxHeight: "80vh", overflowY: "auto" } } : { body: { maxHeight: "75vh", overflowY: "auto" } }}
    >
      {pages.length === 0 ? renderPageUpload() : renderAccumulated()}
    </Modal>
  );
};

export default CreditImageImportModal;
